/**
 * Slack / Critical-path computation pass.
 *
 * Implements `04-` §17 D-13 (standard CPM forward+latest pass).
 *
 * The pass produces a `Map<TaskID, SlackEntry>` from an `AnalysisGraph`.
 * Cycle members are passed in as an `omitSet` — the caller (`analyze.ts`)
 * runs `findCycles` first and provides the omit set per D-15.
 *
 * Time complexity: O((V + E) × calendar_op_cost). The calendar arithmetic
 * is the dominant cost; for v2-perf at 50k tasks we may add per-edge
 * calendar-op memoization in Phase B Day 7 if profiling shows it matters.
 *
 * Day 2: forward pass + helpers.
 * Day 3: latest pass + summary aggregation.
 */

import type { NormalizedTaskID, AnalysisConfig, Calendar, ConstraintType, TimeUnit } from "../types";
import type { AnalysisGraph, SlackEntry } from "../graph/types";
import { topologicalSortWithNodes } from "../utils/toposort";

/**
 * Structural subset of {Analysis,Sched}Edge consumed by the edge-arithmetic
 * helpers. Both AnalysisEdge and SchedEdge satisfy this shape, so the planner
 * (Phase C) reuses these helpers directly with SchedEdge inputs.
 */
export interface EdgeArith {
  readonly lag: number;
  readonly lagUnit: TimeUnit;
  readonly lagCalendar: Calendar;
  readonly sourceLag: number;
  readonly sourceLagUnit: TimeUnit;
  readonly sourceLagCalendar: Calendar;
  readonly targetLag: number;
  readonly targetLagUnit: TimeUnit;
  readonly targetLagCalendar: Calendar;
}

/**
 * Structural subset of {Analysis,Sched}Node consumed by the constraint-bound
 * helpers. SchedNode also satisfies this (its constraintType is always set,
 * but `ConstraintType` is assignable to `ConstraintType | undefined`).
 */
export interface NodeArith {
  readonly constraintType?: ConstraintType;
  readonly constraintDateTs?: number;
  readonly duration: number;
  readonly durationUnit: TimeUnit;
  readonly calendar: Calendar;
}

// ─────────────────────────────────────────────────────────────────────────────
// Edge arithmetic — D-10 per-component calendars
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply an edge's three duration components in their respective calendars
 * (per D-10 + `04-` §15). Used by the forward pass: starting from the
 * source's earliestEnd, compute the resulting target startTs.
 *
 *   target.start ← apply(sourceEnd, sourceLag) then apply(lag) then apply(targetLag)
 *
 * Each component lives in its own calendar; the helper threads the running
 * timestamp through them in order. Skipping zero components is a perf
 * optimization (avoids constructing Date instances and entering the calendar
 * machinery for the common FS case where sourceLag = targetLag = 0).
 */
export function applyEdgeForward(sourceEndTs: number, e: EdgeArith): number {
  let t = sourceEndTs;
  if (e.sourceLag !== 0) {
    t = e.sourceLagCalendar.addDuration(new Date(t), e.sourceLag, e.sourceLagUnit).getTime();
  }
  if (e.lag !== 0) {
    t = e.lagCalendar.addDuration(new Date(t), e.lag, e.lagUnit).getTime();
  }
  if (e.targetLag !== 0) {
    t = e.targetLagCalendar.addDuration(new Date(t), e.targetLag, e.targetLagUnit).getTime();
  }
  return t;
}

/**
 * Like `applyEdgeForward`, but resolves the duration-derived edge components
 * against the endpoints' ACTUAL spans rather than their stored `duration`
 * fields. The two diverge for summaries — a project task's `duration` doesn't
 * track its child-derived span, and the span changes as internal links
 * reschedule its children. `normalize_link` bakes `sourceLag/targetLag =
 * −duration` (SS/SF on the source, FF/SF on the target), so a summary endpoint
 * needs correcting:
 *
 *   - **Source (SS/SF, `sourceLag !== 0`)**: anchor on the source's planned
 *     START (`sourceStartTs`) instead of `end − sourceDuration`. Available in
 *     the block model since a source resolves before its successors.
 *   - **Target (FF/SF, `targetLag !== 0`)**: when `targetSpanMs` is supplied
 *     (the target is a summary), convert the computed target END to the
 *     target START by subtracting the real span, not `targetDuration`.
 *
 * FS edges (both lags 0) are unaffected. Leaf endpoints pass `undefined` for
 * the span / a defined start and fall back to the calendar arithmetic, which
 * is correct since their span equals their duration.
 */
export function applyEdgeForwardFromEndpoints(
  sourceStartTs: number | undefined,
  sourceEndTs: number,
  e: EdgeArith,
  targetSpanMs?: number
): number {
  // Source anchor: SS/SF on the source start (span-agnostic); FS/FF on the end.
  let t: number;
  if (e.sourceLag !== 0 && sourceStartTs !== undefined) {
    t = sourceStartTs;
  } else {
    t = sourceEndTs;
    if (e.sourceLag !== 0) {
      // Fallback (source start unknown): reconstruct from end − sourceDuration.
      t = e.sourceLagCalendar.addDuration(new Date(t), e.sourceLag, e.sourceLagUnit).getTime();
    }
  }
  if (e.lag !== 0) {
    t = e.lagCalendar.addDuration(new Date(t), e.lag, e.lagUnit).getTime();
  }
  if (e.targetLag !== 0) {
    // FF/SF: `t` is now the target's END; convert to its START.
    if (targetSpanMs !== undefined) {
      // Summary target: subtract the real (wall-clock) span — matches how the
      // resolver derives a summary's endTs as `start + (anchorEnd − anchorStart)`.
      t = t - targetSpanMs;
    } else {
      t = e.targetLagCalendar.addDuration(new Date(t), e.targetLag, e.targetLagUnit).getTime();
    }
  }
  return t;
}

/**
 * Apply an edge's three components in reverse for the latest pass
 * (Day 3). Symmetric inverse of applyEdgeForward.
 *   source.end ← apply(target.start, -targetLag) then apply(-lag) then apply(-sourceLag)
 */
export function applyEdgeBackward(targetStartTs: number, e: EdgeArith): number {
  let t = targetStartTs;
  if (e.targetLag !== 0) {
    t = e.targetLagCalendar.addDuration(new Date(t), -e.targetLag, e.targetLagUnit).getTime();
  }
  if (e.lag !== 0) {
    t = e.lagCalendar.addDuration(new Date(t), -e.lag, e.lagUnit).getTime();
  }
  if (e.sourceLag !== 0) {
    t = e.sourceLagCalendar.addDuration(new Date(t), -e.sourceLag, e.sourceLagUnit).getTime();
  }
  return t;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hard-constraint bound application
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply a node's hard constraint as a forward-pass lower bound.
 *
 * Per `04-` §17.1:
 *   SNET/MSO → earliestStart ≥ constraintDate (MSO pins exactly)
 *   FNET     → earliestEnd ≥ constraintDate → earliestStart ≥ constraintDate − duration
 *   FNLT/SNLT → no effect on forward bound (those tighten latest, not earliest)
 *   MFO       → earliestEnd ≥ constraintDate (same as FNET for the lower direction)
 */
export function applyConstraintLowerBound(earliestStartTs: number, node: NodeArith): number {
  const ct = node.constraintType;
  const cd = node.constraintDateTs;
  if (!ct || cd === undefined) return earliestStartTs;
  switch (ct) {
    case "snet":
      return Math.max(earliestStartTs, cd);
    case "mso":
      return cd; // exact pin
    case "fnet":
    case "mfo": {
      // earliest end ≥ cd → earliest start ≥ cd − duration in node's calendar.
      if (node.duration === 0) return Math.max(earliestStartTs, cd);
      const startFromEnd = node.calendar
        .addDuration(new Date(cd), -node.duration, node.durationUnit)
        .getTime();
      return Math.max(earliestStartTs, startFromEnd);
    }
    case "snlt":
    case "fnlt":
    case "asap":
    case "alap":
    default:
      return earliestStartTs;
  }
}

/**
 * Apply a node's hard constraint as a latest-pass upper bound.
 *
 * Per `04-` §17.1:
 *   SNLT/MSO → latestStart ≤ constraintDate → latestEnd ≤ cd + duration (MSO pins exactly)
 *   FNLT/MFO → latestEnd ≤ constraintDate (MFO pins exactly)
 *   SNET/FNET → no effect on latest bound
 */
export function applyConstraintUpperBound(latestEndTs: number, node: NodeArith): number {
  const ct = node.constraintType;
  const cd = node.constraintDateTs;
  if (!ct || cd === undefined) return latestEndTs;
  switch (ct) {
    case "snlt": {
      // start ≤ cd → end ≤ cd + duration in node's calendar.
      if (node.duration === 0) return Math.min(latestEndTs, cd);
      const endFromStart = node.calendar
        .addDuration(new Date(cd), node.duration, node.durationUnit)
        .getTime();
      return Math.min(latestEndTs, endFromStart);
    }
    case "fnlt":
      return Math.min(latestEndTs, cd);
    case "mso": {
      // exact pin: start = cd → end = cd + duration.
      if (node.duration === 0) return cd;
      return node.calendar
        .addDuration(new Date(cd), node.duration, node.durationUnit)
        .getTime();
    }
    case "mfo":
      return cd; // exact pin on end
    case "snet":
    case "fnet":
    case "asap":
    case "alap":
    default:
      return latestEndTs;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Forward pass
// ─────────────────────────────────────────────────────────────────────────────

export interface ForwardResult {
  readonly earliestStartTs: number;
  readonly earliestEndTs: number;
}

/**
 * "Earliest" pass over the analysis graph.
 *
 * Critical-path / slack analysis runs on the tasks' ACTUAL stored positions —
 * it does NOT reschedule them. Each node's earliest start/end is its snapshot
 * start/end (with a hard-constraint lower bound applied). This matches v1's
 * `ext/critical_path/slack.js`, which measures slack from the stored dates
 * (`getDist(source.end_date, target.start_date)` and `dist(task.end,
 * projectEnd)`) rather than from a CPM forward pass.
 *
 * This is deliberately NOT a predecessor-propagating CPM forward pass. For
 * CPM-consistent data (every task at its earliest feasible position, e.g.
 * after auto-scheduling) the two are identical. They diverge only for a task
 * placed earlier than a predecessor would allow (e.g. a "Test" task dragged
 * before the task it depends on): CPM would push it forward and call it
 * critical; v1 keeps it at its actual early date, where it finishes before the
 * project end and therefore has slack. The latest pass (which propagates via
 * successors) plus this actual-date earliest reproduces v1's total/free slack.
 *
 * Cycle members are NOT in the input — the caller (`analyze.ts`) builds
 * the omit set from `findCycles(graph)` before invoking the pass.
 */
export function forwardPass(
  graph: AnalysisGraph,
  omitSet: ReadonlySet<NormalizedTaskID>
): Map<NormalizedTaskID, ForwardResult> {
  const results = new Map<NormalizedTaskID, ForwardResult>();
  for (const [id, node] of graph.nodes) {
    if (omitSet.has(id)) continue;

    // Actual stored start, clamped up by any hard-constraint lower bound.
    const earliestStartTs = applyConstraintLowerBound(node.startDateTs, node);

    const earliestEndTs = node.duration === 0
      ? earliestStartTs
      : node.calendar
          .addDuration(new Date(earliestStartTs), node.duration, node.durationUnit)
          .getTime();

    results.set(id, { earliestStartTs, earliestEndTs });
  }

  return results;
}

// ─────────────────────────────────────────────────────────────────────────────
// Latest pass
// ─────────────────────────────────────────────────────────────────────────────

export interface LatestResult {
  readonly latestStartTs: number;
  readonly latestEndTs: number;
}

/**
 * CPM latest pass over the analysis graph.
 *
 * Walks the topological order in reverse:
 *   1. For terminal nodes (no successor edges): latestEnd = horizonTs.
 *      The horizon is `max(projectEndTs, max earliestEnd across nodes)` —
 *      matches v1 semantics where the late horizon is the actual schedule
 *      completion (not just the configured project_end).
 *   2. For non-terminal nodes: latestEnd = min over successor edges of
 *      `applyEdgeBackward(target.latestStart, edge)`.
 *   3. Apply hard-constraint upper bounds.
 *   4. latestStart = latestEnd − duration in node's calendar.
 */
export function latestPass(
  graph: AnalysisGraph,
  forward: ReadonlyMap<NormalizedTaskID, ForwardResult>,
  projectEndTs: number,
  omitSet: ReadonlySet<NormalizedTaskID>,
  omitEdgePairs: ReadonlySet<string> = new Set()
): Map<NormalizedTaskID, LatestResult> {
  // Determine the horizon. Per the v1-compat note above.
  //
  // Exclude "derived from children" summaries — their `earliestEnd` is
  // degenerate when user data leaves the project's `start_date: null` and
  // the snapshot falls back to `new Date()`. Project-type tasks with
  // `auto_scheduling: false` carry their own fixed dates per
  // docs/guides/custom-projects-dates.md, so they DO participate in the
  // horizon and are flagged by `derivedFromChildren: false` at graph
  // build time. The check matches v1's
  // `isSummaryTask(t) && !isManuallyScheduledSummary(t)`.
  let horizonTs = projectEndTs;
  for (const [id, f] of forward) {
    const node = graph.nodes.get(id);
    if (node?.derivedFromChildren) continue;
    if (f.earliestEndTs > horizonTs) horizonTs = f.earliestEndTs;
  }

  // Topo order (reused). We re-sort here intentionally — the latest pass is
  // logically independent of the forward pass and we don't want to leak the
  // forward pass's internal state.
  const nodeIds: NormalizedTaskID[] = [];
  for (const id of graph.nodes.keys()) {
    if (!omitSet.has(id)) nodeIds.push(id);
  }
  const edgesForToposort: { source: NormalizedTaskID; target: NormalizedTaskID }[] = [];
  for (const e of graph.edges) {
    if (omitSet.has(e.source) || omitSet.has(e.target)) continue;
    if (omitEdgePairs.has(e.source + "|" + e.target)) continue;
    edgesForToposort.push({ source: e.source, target: e.target });
  }
  const order = topologicalSortWithNodes(nodeIds, edgesForToposort);
  if (!order) {
    throw new Error("slack.latestPass: cycles remain after omit set applied");
  }

  const results = new Map<NormalizedTaskID, LatestResult>();
  // Walk in REVERSE topological order.
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i];
    const node = graph.nodes.get(id);
    if (!node) continue;

    let latestEndTs = horizonTs;

    const succs = graph.successors.get(id) ?? [];
    if (succs.length > 0) {
      let bound = Number.POSITIVE_INFINITY;
      for (const e of succs) {
        if (omitSet.has(e.target)) continue;
        if (omitEdgePairs.has(e.source + "|" + e.target)) continue;
        const tgt = results.get(e.target);
        if (!tgt) continue; // defensive — reverse-topo guarantees successors visited
        const candidate = applyEdgeBackward(tgt.latestStartTs, e);
        if (candidate < bound) bound = candidate;
      }
      if (Number.isFinite(bound) && bound < latestEndTs) latestEndTs = bound;
    }

    latestEndTs = applyConstraintUpperBound(latestEndTs, node);

    const latestStartTs = node.duration === 0
      ? latestEndTs
      : node.calendar
          .addDuration(new Date(latestEndTs), -node.duration, node.durationUnit)
          .getTime();

    results.set(id, { latestStartTs, latestEndTs });
  }

  return results;
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary aggregation (D-13)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * After forward+latest passes complete, summaries get their slack from the
 * minimum slack of their descendants per D-13.
 *
 *   summary.totalSlack = min(descendant.totalSlack for descendant in descendants)
 *   summary.freeSlack  = min(descendant.freeSlack  for descendant in descendants)
 *
 * Containment edges (graph.containment) provide the summary → descendant
 * relationship. If a summary has no descendants in the analysis graph (e.g.,
 * all descendants are omitted as cycle members), its pass-derived slack is
 * kept as-is.
 */
function applySummaryAggregation(
  graph: AnalysisGraph,
  slack: Map<NormalizedTaskID, SlackEntry>
): void {
  // Build summary → descendant ids index.
  const descendantsOf = new Map<NormalizedTaskID, NormalizedTaskID[]>();
  for (const c of graph.containment) {
    let arr = descendantsOf.get(c.summary);
    if (!arr) {
      arr = [];
      descendantsOf.set(c.summary, arr);
    }
    arr.push(c.descendant);
  }

  for (const [summary, descendants] of descendantsOf) {
    const sEntry = slack.get(summary);
    if (!sEntry) continue;
    let minTotal = Number.POSITIVE_INFINITY;
    let minFree = Number.POSITIVE_INFINITY;
    for (const d of descendants) {
      const dEntry = slack.get(d);
      if (!dEntry) continue;
      if (dEntry.totalSlack < minTotal) minTotal = dEntry.totalSlack;
      if (dEntry.freeSlack  < minFree)  minFree  = dEntry.freeSlack;
    }
    if (Number.isFinite(minTotal)) {
      slack.set(summary, {
        ...sEntry,
        totalSlack: minTotal,
        freeSlack: Number.isFinite(minFree) ? minFree : minTotal
      });
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// computeSlack entrypoint — full pipeline (Day 3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute slack entries for every non-omitted task.
 *
 * Pipeline:
 *   1. Forward pass → earliestStart/End per node.
 *   2. Latest pass → latestStart/End per node.
 *   3. Derive totalSlack = (latestStart − earliestStart) in minutes.
 *   4. Derive freeSlack = min over successor edges of the slack the task can
 *      absorb without delaying any successor's earliest start.
 *   5. Apply D-12 zero-slack for completed tasks (isCompleted set in graph
 *      builder when useProgress=true && progress=1).
 *   6. Apply D-13 summary aggregation: summary slack = min(descendant slack).
 *   7. isCritical computed later by `computeCriticalPath` in Day 4 — Day 3
 *      leaves it as `false` here (filled by analyze.ts).
 *
 * @param graph         The analysis graph from `buildAnalysisGraph`.
 * @param config        The locked-down `AnalysisConfig` subset.
 * @param projectEndTs  Epoch ms — used as latest-pass terminal default,
 *                      then merged with max(earliestEnd) for the actual horizon.
 * @param omitSet       Task IDs to exclude (cycle members per D-15).
 */
export function computeSlack(
  graph: AnalysisGraph,
  config: AnalysisConfig,
  projectEndTs: number,
  omitSet: ReadonlySet<NormalizedTaskID>,
  omitEdgePairs: ReadonlySet<string> = new Set()
): ReadonlyMap<NormalizedTaskID, SlackEntry> {
  const forward = forwardPass(graph, omitSet);
  const latest = latestPass(graph, forward, projectEndTs, omitSet, omitEdgePairs);

  const result = new Map<NormalizedTaskID, SlackEntry>();
  for (const [id, f] of forward) {
    const l = latest.get(id);
    if (!l) continue; // defensive

    const node = graph.nodes.get(id)!;

    // Total slack: difference between latest and earliest start, in minutes
    // per D-35. Use the node's calendar (matches v1 semantics — slack is
    // measured in the task's working time).
    let totalSlackMinutes: number;
    if (l.latestStartTs === f.earliestStartTs) {
      totalSlackMinutes = 0;
    } else if (l.latestStartTs > f.earliestStartTs) {
      totalSlackMinutes = node.calendar.calculateDuration(
        new Date(f.earliestStartTs),
        new Date(l.latestStartTs),
        "minute"
      );
    } else {
      // Negative slack — over-constrained schedule.
      totalSlackMinutes = -node.calendar.calculateDuration(
        new Date(l.latestStartTs),
        new Date(f.earliestStartTs),
        "minute"
      );
    }

    // Free slack: how much this task can be delayed without affecting any
    // successor's earliest start.
    // freeSlack = min over successor edges of (target.earliestStart − applyEdgeForward(this.earliestEnd, e))
    // No successors → freeSlack = totalSlack.
    let freeSlackMinutes: number;
    const succs = graph.successors.get(id) ?? [];
    const liveSuccs = succs.filter(e => !omitSet.has(e.target) && !omitEdgePairs.has(e.source + "|" + e.target));
    if (liveSuccs.length === 0) {
      freeSlackMinutes = totalSlackMinutes;
    } else {
      let bound = Number.POSITIVE_INFINITY;
      for (const e of liveSuccs) {
        const tgtForward = forward.get(e.target);
        if (!tgtForward) continue;
        const requiredTargetStart = applyEdgeForward(f.earliestEndTs, e);
        const allowableSlipTs = tgtForward.earliestStartTs - requiredTargetStart;
        if (allowableSlipTs < bound) bound = allowableSlipTs;
      }
      // Convert allowable slip from wall-clock ms to minutes-in-calendar.
      if (Number.isFinite(bound)) {
        if (bound <= 0) {
          freeSlackMinutes = 0;
        } else {
          // bound is wall-clock ms — convert to working minutes via calendar.
          freeSlackMinutes = node.calendar.calculateDuration(
            new Date(f.earliestEndTs),
            new Date(f.earliestEndTs + bound),
            "minute"
          );
        }
      } else {
        freeSlackMinutes = totalSlackMinutes;
      }
    }

    // D-12: completed tasks get zero slack.
    if (node.isCompleted) {
      totalSlackMinutes = 0;
      freeSlackMinutes = 0;
    }

    result.set(id, {
      taskId: id,
      earliestStartTs: f.earliestStartTs,
      earliestEndTs: f.earliestEndTs,
      latestStartTs: l.latestStartTs,
      latestEndTs: l.latestEndTs,
      totalSlack: totalSlackMinutes,
      freeSlack: freeSlackMinutes,
      isCritical: false // Day 4's computeCriticalPath populates
    });
  }

  // D-13 summary aggregation: summary slack = min(descendant slack).
  applySummaryAggregation(graph, result);

  return result;
}
